import { useState, useEffect, useCallback } from 'react';

import {
  PORTAL_URL,
  subscribeToConfigChanges
} from '~/common/helpers/environment';

let currentPortalUrl = PORTAL_URL;

// Subscribe to config changes to get updated PORTAL_URL
subscribeToConfigChanges((config: any) => {
  if (config.PORTAL_URL) {
    currentPortalUrl = config.PORTAL_URL;
  }
});

const extractSubdomain = () => {
  if (!currentPortalUrl) return null;

  const { hostname } = window.location;
  const portalUrl = currentPortalUrl?.replace?.(/{([^}]*)}./, '');

  if (hostname?.split?.('.')?.length <= portalUrl?.split?.('.')?.length)
    return null;

  return hostname?.split?.('.')?.[0];
};

export const useReactiveSubdomain = () => {
  const [subdomain, setSubdomain] = useState<string | null>(extractSubdomain);

  const updateSubdomain = useCallback(() => {
    const newSubdomain = extractSubdomain();
    setSubdomain(newSubdomain);
  }, []);

  useEffect(() => {
    // Update on URL changes
    const handlePopState = () => updateSubdomain();
    const handleHashChange = () => updateSubdomain();
    const handleLocationChange = () => updateSubdomain();

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Listen for pushState/replaceState calls
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      handleLocationChange();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      handleLocationChange();
    };

    // Also listen for config changes that might affect PORTAL_URL
    const unsubscribe = subscribeToConfigChanges((config: any) => {
      if (config.PORTAL_URL) {
        currentPortalUrl = config.PORTAL_URL;
        updateSubdomain();
      }
    });

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);

      // Restore original methods
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;

      unsubscribe();
    };
  }, [updateSubdomain]);

  return subdomain;
};
